import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { Popover } from '@hologram-dimension/popover';
import useSimBilling from 'api/sims/useSimBilling';
import useSimDetails from 'api/sims/useSimDetails';
import useSimSmsCount from 'api/sims/useSimSmsCount';
import useSimStatuses from 'api/sims/useSimStatuses';
import BillingPeriodField from 'components/BillingPeriodField/BillingPeriodField';
import IncludedDataField from 'components/IncludedDataField/IncludedDataField';
import { useMemo, type FC } from 'react';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import type { EnumsSimNetworkState } from 'types/__generated__/SimInventoryApi';
import { apiDateToDateTime } from 'utils/dates';
import formatDate from 'utils/dates/formatDate';
import clsx from 'clsx/lite';
import Field from '../Field/Field';
import SimDataUsage from '../SimDataUsage/SimDataUsage';
import styles from './SimUsagePanel.module.css';

interface SimUsagePanelProps {
  hologramSimId?: HologramSimId;
}

const SimUsagePanel: FC<SimUsagePanelProps> = ({ hologramSimId }) => {
  const {
    simDetails,
    error: simDetailsError,
    isLoading: simDetailsIsLoading,
  } = useSimDetails(hologramSimId);

  const {
    networkStatusGroups,
    isLoading: isStatusesLoading,
    error: statusesError,
  } = useSimStatuses();

  const {
    simBilling,
    error: simBillingError,
    isLoading: simBillingIsLoading,
  } = useSimBilling(hologramSimId);

  const {
    smsCountDetails,
    isLoading: isSmsCountLoading,
    error: smsCountError,
  } = useSimSmsCount(hologramSimId);
  const { count: smsCount } = smsCountDetails;

  const { networkStatus } = simDetails;

  const error = simDetailsError || statusesError || simBillingError || smsCountError;
  const isLoading =
    simDetailsIsLoading || isStatusesLoading || simBillingIsLoading || isSmsCountLoading;
  const isPredeployment = networkStatusGroups?.predeployment?.includes(
    networkStatus as EnumsSimNetworkState
  );

  const { smsLimit } = simBilling;

  const { expectedActivationTime, expectedPreflightDuration } = simDetails;

  const { sendAnalyticsEvent } = useAnalytics();

  const panelContents = useMemo(() => {
    if (error) {
      return (
        <PanelNotification
          variant="error"
          label="We ran into an issue loading your SIM"
          actions={
            <Button
              variant="secondary"
              iconStart="Sync"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          }
        >
          We will keep trying in the background or you can try to reload the page.
        </PanelNotification>
      );
    }

    if (isLoading) {
      return <PanelNotification>Loading...</PanelNotification>;
    }

    let smsActualLimit;
    if (isPredeployment) {
      smsActualLimit = 10;
    } else {
      smsActualLimit = smsLimit === -1 ? 'unlimited' : smsLimit;
    }

    return (
      <div className={styles.fieldWrapper}>
        <Field
          label="Data usage"
          value={<SimDataUsage hologramSimId={hologramSimId} />}
          direction="column"
        />
        <IncludedDataField hologramSimId={hologramSimId} />
        <Field
          label="Outbound SMS"
          value={
            <div className={styles.popoverWithTooltip}>
              <span
                className={clsx(
                  (smsLimit ?? -1) !== -1 && (smsCount ?? 0) > (smsLimit ?? -1) && styles.overLimit,
                  styles.numeric
                )}
              >
                {smsCount ?? 0}
              </span>{' '}
              <span className={clsx(styles.diminished, styles.numeric)}>of {smsActualLimit}</span>{' '}
              <Popover
                className={styles.popover}
                id="popover-sms-limit"
                content={
                  isPredeployment ? (
                    'Your SIM will be activated after your pre-deployment testing duration, 100 KB of data usage, or sending 10 outgoing SMS messages, whichever comes first.'
                  ) : (
                    <span>
                      Outbound SMS limits are set per profile on the SIM. Total outbound SMS is
                      displayed across profiles. Please visit{' '}
                      <a
                        href="https://support.hologram.io/hc/en-us/articles/17463264768151-Set-a-device-s-SMS-limit"
                        target="_blank"
                        rel="noreferrer"
                        className={styles.popoverLink}
                      >
                        this article
                      </a>{' '}
                      for information on how to increase your SIM’s outbound SMS limit.
                    </span>
                  )
                }
                onOpen={() => {
                  if (sendAnalyticsEvent) sendAnalyticsEvent('Info - Outbound SMS limit');
                }}
              >
                <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
              </Popover>
            </div>
          }
          direction="column"
        />
        {!isPredeployment && <BillingPeriodField hologramSimId={hologramSimId} />}
        {isPredeployment && (
          <Field
            label="Target activation date (UTC)"
            value={
              <div className={styles.popoverWithTooltip}>
                {expectedPreflightDuration === null ? (
                  <span>Unlimited testing duration</span>
                ) : (
                  <span>
                    {formatDate(expectedActivationTime, 'long')}
                    <span className={styles.diminished}>
                      {' '}
                      ({apiDateToDateTime(expectedActivationTime)?.toRelative({ unit: 'days' })})
                    </span>
                  </span>
                )}
                <Popover
                  className={styles.popover}
                  id="popover-target-activation"
                  content="Your SIM will be activated after your pre-deployment testing duration, 100 KB of data usage, or sending 10 outgoing SMS messages, whichever comes first."
                  onOpen={() => {
                    if (sendAnalyticsEvent) sendAnalyticsEvent('Info - Target activation date');
                  }}
                >
                  <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
                </Popover>
              </div>
            }
            direction="column"
          />
        )}
      </div>
    );
  }, [
    error,
    expectedActivationTime,
    expectedPreflightDuration,
    hologramSimId,
    isLoading,
    isPredeployment,
    sendAnalyticsEvent,
    smsCount,
    smsLimit,
  ]);

  return (
    <Panel
      header={isPredeployment ? 'Pre-deployment data usage' : 'Usage this billing period'}
      isLoading={isLoading}
    >
      {panelContents}
    </Panel>
  );
};

export default SimUsagePanel;
